import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Webcam } from 'lucide-react';
import { useAuth } from '../../../features/auth/AuthContext';
import useEnterpriseProctoring from '../../../hooks/useEnterpriseProctoring';
import ProctoringAlertBanner from '../../../components/assessment/ProctoringAlertBanner';
import ProctoringStatusPanel from '../../../components/assessment/ProctoringStatusPanel';
import {
  SKILL_ASSESSMENT_NAME,
  SKILL_SECTIONS,
  completeSkillAssessment,
  startSkillAssessment,
  submitSkillSection
} from '../../../services/assessmentService';
import StandardMCQRenderer from '../../../components/assessment/renderers/StandardMCQRenderer';
import EmailSnippetRenderer from '../../../components/assessment/renderers/EmailSnippetRenderer';
import ScenarioCaseRenderer from '../../../components/assessment/renderers/ScenarioCaseRenderer';
import LanguageSelector from '../../../components/ui/LanguageSelector';
import { useAssessmentTranslation } from '../../../hooks/useAssessmentTranslation';
import { getUIString } from '../../../config/assessmentUIStrings';
import { buildDisplayQuestion } from '../../../utils/translationDisplay';

const SECTION_SECONDS = 15 * 60;

const formatHms = (seconds) => {
  const hh = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

export default function TestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // ADDED: hidden proctoring video ref used by enterprise proctoring hook.
  const videoRef = useRef(null);
  const webcamRef = useRef(null);
  const testStreamRef = useRef(null);
  const forceSubmitRef = useRef(() => {});
  const questionStartedAtRef = useRef(Date.now());

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [questionSets, setQuestionSets] = useState({});
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sectionTimeLeft, setSectionTimeLeft] = useState(SECTION_SECONDS);
  const [answers, setAnswers] = useState({});
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [lastAnswerTimestamp, setLastAnswerTimestamp] = useState(Date.now());

  const currentSection = SKILL_SECTIONS[currentSectionIndex];
  const currentQuestions = questionSets[currentSection?.code] || [];
  const currentQuestion = currentQuestions[currentQuestionIndex];
  const upcomingQuestions = currentQuestions.slice(currentQuestionIndex + 1, currentQuestionIndex + 3);

  // ADDED: enterprise proctoring replaces page-level tab switch and rapid answer placeholders.
  const {
    proctoringReport,
    currentAlert,
    dismissAlert,
    violations,
    integrityScore,
    faceStatus,
    gazeStatus,
    isFullscreen,
    requestFullscreen,
    recordAnswer
  } = useEnterpriseProctoring({
    assessmentKey: 'skill',
    videoRef,
    enabled: true,
    onViolation: (type, detail) => {
      console.warn('[Proctoring]', type, detail);
    },
    onForcedSubmit: () => forceSubmitRef.current?.(),
    maxViolations: 10
  });

  const totalAnswered = useMemo(
    () => Object.values(answers).reduce((sum, sectionMap) => sum + Object.keys(sectionMap || {}).length, 0),
    [answers]
  );
  const {
    currentLanguage,
    isTranslating,
    translatedContent,
    switchLanguage,
    translateCurrentQuestion
  } = useAssessmentTranslation();

  useEffect(() => {
    const photo = sessionStorage.getItem('skill_verify_photo');
    if (!photo) {
      navigate('/assessment/skill/verify');
      return;
    }

    const boot = async () => {
      try {
        const payload = await startSkillAssessment({ userId: user?.uid || 'anonymous', profilePhoto: photo || null });
        setSessionId(payload.sessionId);
        setQuestionSets(payload.questionSets || {});
        sessionStorage.setItem('skill_session_id', payload.sessionId);
      } catch (error) {
        setLoadError(error?.response?.data?.message || 'Unable to start skill session.');
      } finally {
        setLoading(false);
      }
    };

    boot();
  }, [navigate, user?.uid]);

  useEffect(() => {
    questionStartedAtRef.current = Date.now();
  }, [currentSectionIndex, currentQuestionIndex]);

  useEffect(() => {
    if (currentQuestion) {
      translateCurrentQuestion(currentQuestion);
    }
  }, [currentQuestion?.id, currentLanguage, translateCurrentQuestion]);

  useEffect(() => {
    if (loading || !sessionId) return undefined;

    const interval = setInterval(() => {
      setSectionTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSubmitSection(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [loading, sessionId, currentSectionIndex]);

  // REMOVED: replaced by useEnterpriseProctoring visibility/tab monitoring.

  useEffect(() => {
    let liveStream;
    const setupWebcam = async () => {
      try {
        setCameraError(false);
        liveStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        testStreamRef.current = liveStream;
        if (videoRef.current) {
          videoRef.current.srcObject = liveStream;
        }
        const video = webcamRef.current;
        if (video) {
          setCameraReady(false);
          video.srcObject = liveStream;
          video.onloadedmetadata = async () => {
            try {
              await video.play();
            } catch (_e) {
              // autoplay can be blocked silently
            }
            setCameraReady(Boolean(video.videoWidth && video.videoHeight));
          };
        }
      } catch (err) {
        setCameraError(true);
        setCameraReady(false);
        console.warn('Skill webcam init failed:', err?.message || err);
      }
    };

    setupWebcam();

    return () => {
      if (testStreamRef.current) {
        testStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (webcamRef.current) {
        webcamRef.current.srcObject = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      setCameraReady(false);
    };
  }, []);

  const saveAnswer = (payload) => {
    if (!currentQuestion) return;

    setAnswers((prev) => ({
      ...prev,
      [currentSection.code]: {
        ...(prev[currentSection.code] || {}),
        [currentQuestion.id]: {
          ...payload,
          timeTaken: payload.timeTaken ?? Math.max(1, Math.round((Date.now() - questionStartedAtRef.current) / 1000))
        }
      }
    }));
    // ADDED: enterprise proctoring rapid-answer capture.
    recordAnswer(Date.now() - lastAnswerTimestamp);
    setLastAnswerTimestamp(Date.now());
  };

  const goNext = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const goPrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmitSection = async (autoAdvance = false) => {
    if (!sessionId || !currentSection || submitting) return;
    setSubmitting(true);

    try {
      const sectionAnswers = (questionSets[currentSection.code] || []).map((question) => {
        const existing = answers[currentSection.code]?.[question.id] || {};
        return {
          questionId: question.id,
          selectedOption: existing.selectedOption || null,
          timeTaken: existing.timeTaken || 0
        };
      });

      await submitSkillSection({
        sessionId,
        sectionCode: currentSection.code,
        answers: sectionAnswers
      });

      if (currentSectionIndex >= SKILL_SECTIONS.length - 1) {
        const completed = await completeSkillAssessment({
          sessionId,
          profilePhoto: verifyPhoto || null,
          // MODIFIED: proctoringData now from useEnterpriseProctoring hook.
          proctoringData: proctoringReport
        });
        sessionStorage.setItem('skill_result_payload', JSON.stringify(completed));
        navigate('/assessment/skill/result');
        return;
      }

      setCurrentSectionIndex((prev) => prev + 1);
      setCurrentQuestionIndex(0);
      setSectionTimeLeft(SECTION_SECONDS);
    } finally {
      if (!autoAdvance) {
        setSubmitting(false);
      } else {
        setTimeout(() => setSubmitting(false), 300);
      }
    }
  };

  if (loading) {
    return <div className="min-h-screen skill-intro-bg flex items-center justify-center text-white">Loading assessment...</div>;
  }

  if (loadError) {
    return (
      <div className="min-h-screen skill-intro-bg flex flex-col items-center justify-center text-white px-4">
        <p>{loadError}</p>
        <button
          type="button"
          onClick={() => navigate('/assessment')}
          className="mt-4 rounded-lg border border-white/30 bg-white/10 px-4 py-2"
        >
          Back to Hub
        </button>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="min-h-screen skill-intro-bg flex items-center justify-center text-white">Question set not available.</div>;
  }

  const currentSaved = answers[currentSection.code]?.[currentQuestion.id] || null;
  const displayQuestion = buildDisplayQuestion(currentQuestion, translatedContent);
  const t = (key) => getUIString(currentLanguage, key);
  const isEmail = currentSection.code === 'SKL_CS' || currentQuestion.renderer === 'email_snippet';
  const isScenario = currentSection.code === 'SKL_PE' || currentQuestion.renderer === 'scenario_case';
  const verifyPhoto = sessionStorage.getItem('skill_verify_photo') || '';
  forceSubmitRef.current = () => {
    if (!submitting) {
      handleSubmitSection(false);
    }
  };

  return (
    <div className="min-h-screen skill-intro-bg text-white p-3 md:p-5">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        aria-hidden="true"
      />
      <ProctoringAlertBanner message={currentAlert} onDismiss={dismissAlert} />
      <ProctoringStatusPanel
        faceStatus={faceStatus}
        gazeStatus={gazeStatus}
        violations={violations}
        integrityScore={integrityScore}
        isFullscreen={isFullscreen}
        onRequestFullscreen={requestFullscreen}
        videoRef={videoRef}
      />
      <div className="max-w-[1320px] mx-auto grid gap-4 md:grid-cols-[240px_1fr_200px]">
        <aside className="skill-panel p-4 rounded-2xl h-fit">
          <h2 className="font-jakarta text-lg">{SKILL_ASSESSMENT_NAME}</h2>
          <p className={`mt-3 text-2xl font-semibold ${sectionTimeLeft <= 300 ? 'timer-warning text-red-400' : 'text-white'}`}>{formatHms(sectionTimeLeft)}</p>

          <div className="mt-4 space-y-2">
            {SKILL_SECTIONS.map((section, idx) => {
              const answeredInSection = Object.keys(answers[section.code] || {}).length;
              return (
                <button
                  key={section.code}
                  type="button"
                  onClick={() => {
                    if (idx <= currentSectionIndex) {
                      setCurrentSectionIndex(idx);
                      setCurrentQuestionIndex(0);
                    }
                  }}
                  className={`section-nav-item w-full text-left px-3 py-2 rounded-lg border ${idx === currentSectionIndex ? 'border-emerald-400/60 bg-emerald-500/20' : 'border-white/15 bg-white/5'}`}
                >
                  <p className="text-sm font-medium">{section.name}</p>
                  <p className="text-xs text-white/65">{answeredInSection}/10 answered</p>
                </button>
              );
            })}
          </div>

          <div className="mt-4">
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="progress-fill h-full bg-gradient-to-r from-emerald-500 to-teal-600" style={{ width: `${(totalAnswered / 50) * 100}%` }} />
            </div>
            <p className="mt-2 text-xs text-white/70">Overall progress: {totalAnswered}/50</p>
          </div>
        </aside>

        <main className="skill-panel p-4 md:p-6 rounded-2xl assessment-shell">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h3 className="font-jakarta text-xl">{currentSection.name}</h3>
              {currentLanguage !== 'en' ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-blue-400/70 bg-blue-400/10 border border-blue-400/20 rounded-full px-2 py-0.5">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10" />
                  </svg>
                  {t('translated')}
                </span>
              ) : null}
            </div>
            <p className="text-sm text-white/70">Question {currentQuestionIndex + 1} of 10</p>
            <LanguageSelector
              currentLanguage={currentLanguage}
              onLanguageChange={(langCode) => switchLanguage(langCode, currentQuestion, upcomingQuestions)}
              isTranslating={isTranslating}
            />
          </div>

          <div className={`mt-4 transition-opacity duration-200 ${isTranslating ? 'opacity-40' : 'opacity-100'}`}>
            {isEmail ? (
              <EmailSnippetRenderer
                question={displayQuestion}
                value={currentSaved?.selectedOption || ''}
                onChange={(selectedOption) => saveAnswer({ selectedOption })}
              />
            ) : null}

            {!isEmail && isScenario ? (
              <ScenarioCaseRenderer
                question={displayQuestion}
                value={currentSaved?.selectedOption || ''}
                onChange={(selectedOption) => saveAnswer({ selectedOption })}
              />
            ) : null}

            {!isEmail && !isScenario ? (
              <StandardMCQRenderer
                question={displayQuestion}
                value={currentSaved?.selectedOption || ''}
                onChange={(selectedOption) => saveAnswer({ selectedOption })}
              />
            ) : null}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 justify-between">
            <button type="button" onClick={goPrevious} disabled={currentQuestionIndex === 0} className="px-4 py-2 rounded-lg border border-white/20 bg-white/10 disabled:opacity-40">
              ← Previous
            </button>

            <div className="flex items-center gap-2">
              <button type="button" onClick={goNext} disabled={currentQuestionIndex >= 9} className="px-4 py-2 rounded-lg border border-white/20 bg-white/10 disabled:opacity-40">
                {t('nextQuestion')} →
              </button>
              <button
                type="button"
                onClick={() => handleSubmitSection(false)}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 font-semibold disabled:opacity-50"
              >
                {currentSectionIndex === SKILL_SECTIONS.length - 1 ? t('submitAssessment') : t('submitSection')}
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {currentQuestions.map((q, idx) => {
              const answered = Boolean(answers[currentSection.code]?.[q.id]);
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setCurrentQuestionIndex(idx)}
                  className={`q-dot h-8 w-8 rounded-full text-xs border ${idx === currentQuestionIndex ? 'current border-white bg-white/25' : answered ? 'answered border-emerald-300 bg-emerald-500/35' : 'border-white/30 bg-white/10'}`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
        </main>

        <aside className="skill-panel p-4 rounded-2xl h-fit">
          <div className="relative h-40 rounded-xl overflow-hidden border border-white/20 bg-black/40">
            <video ref={webcamRef} autoPlay playsInline muted onLoadedData={() => setCameraReady(true)} className="w-full h-full object-cover" />
            {!cameraReady ? <div className="absolute inset-0 flex items-center justify-center text-xs text-white/70 bg-black/35">Camera loading...</div> : null}
            {!cameraReady && cameraError && verifyPhoto ? (
              <img src={verifyPhoto} alt="Verification capture" className="absolute inset-0 h-full w-full object-cover opacity-90" />
            ) : null}
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-emerald-300">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            Proctoring Active
          </div>
          <p className="mt-2 text-xs text-white/65">Tab switches flagged: {proctoringReport.tab_switches}</p>
          <div className="mt-3 text-xs text-white/65 flex items-center gap-2">
            <Webcam className="h-4 w-4" />
            {cameraReady ? 'Live webcam monitoring enabled' : cameraError ? 'Live feed unavailable, using verification photo' : 'Live webcam monitoring enabled'}
          </div>
        </aside>
      </div>
    </div>
  );
}
